import React, { useState, useEffect, useCallback, useRef } from 'react';

export interface CarouselProps {
  children: React.ReactNode[];
  orientation?: 'horizontal' | 'vertical';
  autoPlay?: boolean;
  autoPlayInterval?: number;
  showNavigation?: boolean;
  className?: string;
  itemClassName?: string;
}

/**
 * Carousel component that supports horizontal/vertical orientation,
 * auto-play, and navigation arrows. Can display any HTML content.
 */
export const Carousel: React.FC<CarouselProps> = ({
  children,
  orientation = 'horizontal',
  autoPlay = false,
  autoPlayInterval = 3000,
  showNavigation = true,
  className = '',
  itemClassName = '',
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);

  const items = React.Children.toArray(children);
  const totalItems = items.length;

  // Navigate to next item
  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % totalItems);
  }, [totalItems]);

  // Navigate to previous item
  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + totalItems) % totalItems);
  }, [totalItems]);

  // Navigate to specific index
  const goToIndex = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  // Auto-play functionality
  useEffect(() => {
    if (autoPlay && !isHovering && totalItems > 1) {
      autoPlayRef.current = setInterval(goToNext, autoPlayInterval);
    }

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [autoPlay, isHovering, autoPlayInterval, goToNext, totalItems]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, []);

  const isHorizontal = orientation === 'horizontal';
  const showArrows = showNavigation && totalItems > 1;

  return (
    <div
      className={`carousel-container relative ${className}`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Carousel viewport */}
      <div className={`carousel-viewport overflow-hidden relative ${isHorizontal ? 'w-full' : 'h-96'}`}>
        <div
          className={`carousel-track flex transition-transform duration-500 ease-in-out ${
            isHorizontal ? 'flex-row' : 'flex-col'
          }`}
          style={{
            transform: isHorizontal
              ? `translateX(-${currentIndex * 100}%)`
              : `translateY(-${currentIndex * 100}%)`,
          }}
        >
          {items.map((item, index) => (
            <div
              key={index}
              className={`carousel-item flex-shrink-0 ${
                isHorizontal ? 'w-full' : 'h-96'
              } ${itemClassName}`}
            >
              {item}
            </div>
          ))}
        </div>
      </div>

      {/* Previous button */}
      {showArrows && (
        <button
          onClick={goToPrevious}
          className={`carousel-nav-btn absolute z-10 bg-white/90 hover:bg-white text-slate-700 rounded-full p-2 shadow-lg transition-all hover:scale-110 ${
            isHorizontal
              ? 'left-2 top-1/2 -translate-y-1/2'
              : 'top-2 left-1/2 -translate-x-1/2'
          }`}
          aria-label="Previous"
        >
          {isHorizontal ? (
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          ) : (
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 15l7-7 7 7"
              />
            </svg>
          )}
        </button>
      )}

      {/* Next button */}
      {showArrows && (
        <button
          onClick={goToNext}
          className={`carousel-nav-btn absolute z-10 bg-white/90 hover:bg-white text-slate-700 rounded-full p-2 shadow-lg transition-all hover:scale-110 ${
            isHorizontal
              ? 'right-2 top-1/2 -translate-y-1/2'
              : 'bottom-2 left-1/2 -translate-x-1/2'
          }`}
          aria-label="Next"
        >
          {isHorizontal ? (
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          ) : (
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          )}
        </button>
      )}

      {/* Indicators */}
      {totalItems > 1 && (
        <div
          className={`carousel-indicators absolute z-10 flex gap-2 ${
            isHorizontal
              ? 'bottom-4 left-1/2 -translate-x-1/2'
              : 'right-4 top-1/2 -translate-y-1/2 flex-col'
          }`}
        >
          {items.map((_, index) => (
            // Outer button provides a 24×24px minimum touch target (WCAG 2.5.5).
            // The visible dot is a smaller decorative element inside it.
            <button
              key={index}
              onClick={() => goToIndex(index)}
              className="flex items-center justify-center w-6 h-6 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
              aria-label={`Go to slide ${index + 1}`}
              aria-current={index === currentIndex ? 'true' : undefined}
            >
              <span
                className={`transition-all rounded-full ${
                  index === currentIndex
                    ? 'bg-white w-4 h-2'
                    : 'bg-white/50 hover:bg-white/75 w-2 h-2'
                }`}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default Carousel;